import { Injectable, OnDestroy } from '@angular/core';
import { AuthorizationService, DEVICE_OS_FAMILY, FeatureToggleService } from '@nx-fe/services';
import { Subject } from 'rxjs';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { SotiAssistStatus } from 'app/modules/remote-control-ng';
import { XsI18nPipe } from '@nx-fe/pipes';
import { RemoteControlDeviceState, DeviceStateRegistryService } from './device-state-registry.service';

/** Provides current remote control session state and related functionality */
@Injectable()
export class RemoteControlSessionService implements OnDestroy {
  public deviceLogAvailableAgentVersion = '14.0.0';
  public deviceLogCategoriesAvailableAgentVersion = '14.2.3';
  public deviceLogAvailableOsVersion = '5.0.0';

  // [mkotormus]: FIXME: discuss how to port this (or still need to hook-up to new 'pop-out' button)
  private standAlone = false;

  /**
   * Emits whenever user starts interacting with the device screen. This is useful for some of the UI elements
   * in RC window, such as pop-ups over the extended toolbar, which will dismiss themselves when this occurs.
   */
  public deviceScreenFocused = new Subject<void>();

  public get rcState(): RemoteControlDeviceState {
    return this.deviceStateRegistry.getCurrentState();
  }

  public get isStandalone(): boolean {
    return (this.rcState && this.standAlone && this.rcState && !this.rcState.poppedOut) || this.standAlone;
  }

  constructor(
    private deviceStateRegistry: DeviceStateRegistryService,
    private featureToggleService: FeatureToggleService,
    private authorizationService: AuthorizationService,
    private saI18nPipe: XsI18nPipe
  ) {}

  /* istanbul ignore next */
  public ngOnDestroy(): void {
    this.deviceScreenFocused.complete();
  }

  public get isAndroidDevice(): boolean {
    return this.rcState?.device?.family === DEVICE_OS_FAMILY.AndroidPlus;
  }

  public get isDeviceLogSupported(): boolean {
    if (this.rcState?.device == null) {
      return false;
    }
    const isCompatibleAgentVersion = this.compareAgentVersion(this.deviceLogAvailableAgentVersion) >= 0;
    const isCompatibleOs = this.compareOsVersion(this.deviceLogAvailableOsVersion) >= 0;
    const isCompatibleDeviceKind =
      (this.rcState.device.kind === 'AndroidPlus' ||
        this.rcState.device.kind === 'AndroidForWork' ||
        this.rcState.device.kind === 'AndroidElm');

    const result = this.isAndroidDevice && isCompatibleAgentVersion && isCompatibleOs && isCompatibleDeviceKind;
    return result;
  }

  public get isDeviceLogCategoriesSupported(): boolean {
    return (
      this.featureToggleService.isEnabled('RemoteControlDeviceLogsCategories') &&
      this.authorizationService.isAnyGroupAdminOrTechnician() &&
      this.isDeviceLogSupported &&
      this.compareAgentVersion(this.deviceLogCategoriesAvailableAgentVersion) >= 0
    );
  }

  public compareAgentVersion(version: string): number {
    const agentVersion = this._computeAgentVersion();
    return this._compareVersionNumbers(agentVersion, version);
  }

  public compareOsVersion(version: string): number {
    const osVersion = (this.rcState && this.rcState.device && this.rcState.device.osVersion) || '0.0.0';
    return this._compareVersionNumbers(osVersion, version);
  }

  public compareMcVersion(version: string): number {
    const McVersion = (this.rcState && this.rcState.device && this.rcState.device.mcVersion) || '0.0.0';
    return this._compareVersionNumbers(McVersion, version);
  }

  /* istanbul ignore next */
  public onCanvasMouseDown() {
    this.deviceScreenFocused.next();
  }

  private _validateParts = (parts): boolean => {
    for (const part of parts) {
      if (!/^\d+$/.test(part)) {
        return false;
      }
    }
    return true;
  };

  private _compareVersionNumbers = (v1: string, v2: string): number => {
    if (!v1 || !v2) {
      return -1;
    }

    const v1parts = v1.split('.');
    const v2parts = v2.split('.');

    // First, validate both numbers are true version numbers
    if (!this._validateParts(v1parts) || !this._validateParts(v2parts)) {
      return -1;
    }

    const len = Math.max(v1parts.length, v2parts.length);

    // Normalize lengths with 0 components
    // This ensures 10.0.1 > 10
    if (v1parts.length < v2parts.length) {
      for (let i = 0; i < len; i++) {
        v1parts.push('0');
      }
    } else if (v2parts.length < v1parts.length) {
      for (let i = 0; i < len; i++) {
        v2parts.push('0');
      }
    }

    // loop while the components are equal
    for (let i = 0; i < len; i++) {
      // A bigger than B
      if (parseInt(v1parts[i], 10) > parseInt(v2parts[i], 10)) {
        return 1;
      }

      // B bigger than A
      if (parseInt(v1parts[i], 10) < parseInt(v2parts[i], 10)) {
        return -1;
      }
    }

    // Otherwise they are the same.
    return 0;
  };

  private _computeAgentVersion = (): string => {
    try {
      const versionHi = RcContext.instance.versionHi;
      const versionLo = RcContext.instance.versionLo;
      if (typeof versionHi !== 'undefined' && typeof versionLo !== 'undefined') {
        const major = versionHi;
        const lo = versionLo;
        const minor = Math.floor(lo / 10);
        const build = lo % 10;
        return `${major}.${minor}.${build}`;
      }
    } catch {
      return '0.0.0';
    }
    return '0.0.0';
  };

  public mapServerStatusMessage(status: SotiAssistStatus): string {
    switch (status) {
      case SotiAssistStatus.SasVideoFolderCreationFailed:
        return this.saI18nPipe.transform('remoteControl_VideoFolderCreationFailed');
      case SotiAssistStatus.SasVideoEncodingServerOccupied:
        return this.saI18nPipe.transform('remoteControl_VideoEncodingServerOccupied');
      case SotiAssistStatus.SasVideoMaxDurationReached:
        return this.saI18nPipe.transform('remoteControl_VideoMaxDurationReached');
      case SotiAssistStatus.SasLogFileAttachmentCreationFailed:
        return this.saI18nPipe.transform('remoteControl_VideoAttachmentCreationFailed');
      case SotiAssistStatus.SasLogFileUnavailableOnAgent:
        return this.saI18nPipe.transform('remoteControl_DeviceLogsFileUnavailableInAgent');
      case SotiAssistStatus.SasDownloadLinkCreationFailed:
        return this.saI18nPipe.transform('remoteControl_DownloadLinkCreationFailed');
      case SotiAssistStatus.SasVideoRecordingEmpty:
        return this.saI18nPipe.transform('remoteControl_VideoRecordingEmpty');
      case SotiAssistStatus.SasVideoFolderPathTooLong:
        return this.saI18nPipe.transform('remoteControl_VideoFolderPathTooLong');
      default:
        return this.saI18nPipe.transform('remoteControl_VideoRecordingUnknownServerSetatus');
    }
  }
}
